'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, UserPlus, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

interface RegisterFormProps {
  onSwitch: () => void;
}

export default function RegisterForm({ onSwitch }: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registeredName, setRegisteredName] = useState('');

  const { register, handleSubmit, watch, formState: { errors } } = useForm<RegisterData>();
  const password = watch('password');

  const sendVerificationEmail = async (email: string, name: string) => {
    // 1. Create a verification token
    const tokenRes = await fetch('/api/verify-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'create', email, name }),
    });
    const tokenData = await tokenRes.json();
    if (!tokenRes.ok || !tokenData.token) {
      throw new Error('Failed to generate verification token');
    }

    // 2. Build the verification URL
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
    const verificationUrl = `${siteUrl}/verify-email?token=${tokenData.token}`;

    // 3. Send the email
    const emailRes = await fetch('/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'email_verification',
        to: email,
        name,
        verificationUrl,
      }),
    });
    const emailResult = await emailRes.json();
    if (!emailRes.ok) {
      console.error('[RegisterForm] Verification email failed:', emailResult);
      // Surface the actual Brevo error message for easier debugging
      const msg = emailResult?.details?.message || emailResult?.error || 'Failed to send verification email';
      throw new Error(msg);
    }
    console.log('[RegisterForm] Verification email sent. MessageId:', emailResult.messageId);
  };

  const onSubmit = async (data: RegisterData) => {
    setIsLoading(true);
    setEmailError(false);
    try {
      // BACKEND INTEGRATION: POST /api/auth/register with applicant data
      await new Promise(r => setTimeout(r, 800));

      const fullName = `${data.firstName} ${data.lastName}`;
      setRegisteredEmail(data.email);
      setRegisteredName(fullName);

      await sendVerificationEmail(data.email, fullName);

      setVerificationSent(true);
    } catch (err) {
      console.error('[RegisterForm] Registration error:', err);
      // Show error state — do NOT proceed to verification screen
      setEmailError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setEmailError(false);
    try {
      await sendVerificationEmail(registeredEmail, registeredName);
      setVerificationSent(true);
      toast.success('Verification email sent! Please check your inbox.');
    } catch (err) {
      console.error('[RegisterForm] Resend error:', err);
      setEmailError(true);
      toast.error('Failed to send verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  // ── Email send error screen ─────────────────────────────────────────────────
  if (emailError && registeredEmail) {
    return (
      <div className="animate-fade-in text-center">
        <div className="mb-6">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fee2e2' }}>
            <Mail size={32} style={{ color: '#dc2626' }} />
          </div>
          <h1 className="text-2xl font-700 text-foreground mb-2" style={{ fontWeight: 700 }}>
            Email Not Sent
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Your account was created, but we could not send the verification email to
          </p>
          <p className="text-sm font-600 mt-1" style={{ color: 'var(--primary)', fontWeight: 600 }}>
            {registeredEmail}
          </p>
        </div>

        <div className="rounded-lg p-4 mb-6 text-left" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
          <p className="text-sm font-600 mb-1" style={{ color: '#991b1b', fontWeight: 600 }}>What to do:</p>
          <ol className="text-sm space-y-1 list-decimal list-inside leading-relaxed" style={{ color: '#7f1d1d' }}>
            <li>Click <strong>"Resend Verification Email"</strong> below</li>
            <li>Check your spam or junk folder</li>
            <li>Contact <a href="mailto:hr@2cheze.co.ke" style={{ color: '#1e3a5f', textDecoration: 'underline' }}>hr@2cheze.co.ke</a> if the issue persists</li>
          </ol>
        </div>

        <button
          onClick={handleResend}
          disabled={isResending}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-600 transition-colors mb-4"
          style={{ backgroundColor: 'var(--primary)', color: '#ffffff', fontWeight: 600 }}
        >
          {isResending ? (
            <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending...</>
          ) : (
            <><RefreshCw size={15} /> Resend Verification Email</>
          )}
        </button>

        <p className="text-sm text-muted-foreground">
          Wrong email?{' '}
          <button
            onClick={() => { setEmailError(false); setRegisteredEmail(''); setRegisteredName(''); }}
            className="font-600 hover:underline"
            style={{ color: 'var(--primary)', fontWeight: 600 }}
          >
            Go back
          </button>
        </p>
      </div>
    );
  }

  // ── Email verification pending screen ──────────────────────────────────────
  if (verificationSent) {
    return (
      <div className="animate-fade-in text-center">
        <div className="mb-6">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#dbeafe' }}>
            <Mail size={32} style={{ color: '#1e3a5f' }} />
          </div>
          <h1 className="text-2xl font-700 text-foreground mb-2" style={{ fontWeight: 700 }}>
            Check Your Email
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            We&apos;ve sent a verification link to
          </p>
          <p className="text-sm font-600 mt-1" style={{ color: 'var(--primary)', fontWeight: 600 }}>
            {registeredEmail}
          </p>
        </div>

        <div className="rounded-lg p-4 mb-6 text-left" style={{ backgroundColor: '#f0f4f8' }}>
          <p className="text-sm font-600 mb-2" style={{ color: '#1e3a5f', fontWeight: 600 }}>Next Steps:</p>
          <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside leading-relaxed">
            <li>Open the email from MM Shah &amp; MV Shah Academy</li>
            <li>Click the <strong>&quot;Verify My Email Address&quot;</strong> button</li>
            <li>You will be redirected to start your application</li>
          </ol>
        </div>

        <div className="rounded-lg p-3 mb-6 text-sm" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
          ⏰ The link expires in <strong>24 hours</strong>. Check your spam folder if you don&apos;t see it.
        </div>

        <button
          onClick={handleResend}
          disabled={isResending}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm font-600 transition-colors mb-4"
          style={{ borderColor: 'var(--primary)', color: 'var(--primary)', fontWeight: 600 }}
        >
          {isResending ? (
            <><div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> Resending...</>
          ) : (
            <><RefreshCw size={15} /> Resend Verification Email</>
          )}
        </button>

        <p className="text-sm text-muted-foreground">
          Wrong email?{' '}
          <button
            onClick={() => setVerificationSent(false)}
            className="font-600 hover:underline"
            style={{ color: 'var(--primary)', fontWeight: 600 }}
          >
            Go back
          </button>
        </p>
      </div>
    );
  }

  // ── Registration form ───────────────────────────────────────────────────────
  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-700 text-foreground mb-1" style={{ fontWeight: 700 }}>
          Create Account
        </h1>
        <p className="text-sm text-muted-foreground">
          Register to submit your employment application.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label">First Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              className={`form-input ${errors.firstName ? 'error' : ''}`}
              placeholder="Amina"
              {...register('firstName', { required: 'Required' })}
            />
            {errors.firstName && <p className="form-error">{errors.firstName.message}</p>}
          </div>
          <div>
            <label className="form-label">Last Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              className={`form-input ${errors.lastName ? 'error' : ''}`}
              placeholder="Ochieng"
              {...register('lastName', { required: 'Required' })}
            />
            {errors.lastName && <p className="form-error">{errors.lastName.message}</p>}
          </div>
        </div>

        <div>
          <label className="form-label">Email Address <span className="text-red-500">*</span></label>
          <input
            type="email"
            className={`form-input ${errors.email ? 'error' : ''}`}
            placeholder="you@gmail.com"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
            })}
          />
          {errors.email && <p className="form-error">{errors.email.message}</p>}
        </div>

        <div>
          <label className="form-label">Phone Number <span className="text-red-500">*</span></label>
          <input
            type="tel"
            className={`form-input ${errors.phone ? 'error' : ''}`}
            placeholder="+254 7XX XXX XXX"
            {...register('phone', { required: 'Phone number is required' })}
          />
          {errors.phone && <p className="form-error">{errors.phone.message}</p>}
        </div>

        <div>
          <label className="form-label">Password <span className="text-red-500">*</span></label>
          <p className="form-helper mb-1">Minimum 8 characters with uppercase, number, and symbol.</p>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className={`form-input pr-10 ${errors.password ? 'error' : ''}`}
              placeholder="Create a strong password"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Minimum 8 characters' },
                pattern: {
                  value: /^(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/,
                  message: 'Must include uppercase, number, and symbol',
                },
              })}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="form-error">{errors.password.message}</p>}
        </div>

        <div>
          <label className="form-label">Confirm Password <span className="text-red-500">*</span></label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              className={`form-input pr-10 ${errors.confirmPassword ? 'error' : ''}`}
              placeholder="Re-enter your password"
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: v => v === password || 'Passwords do not match',
              })}
            />
            <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="form-error">{errors.confirmPassword.message}</p>}
        </div>

        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="terms"
            className="w-4 h-4 mt-0.5 rounded border-input accent-primary flex-shrink-0"
            {...register('terms', { required: 'You must accept the terms to continue' })}
          />
          <label htmlFor="terms" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
            I agree to the{' '}
            <span className="text-primary font-500 cursor-pointer hover:underline" style={{ color: 'var(--primary)' }}>Privacy Policy</span>
            {' '}and consent to my personal data being processed for recruitment purposes.
          </label>
        </div>
        {errors.terms && <p className="form-error">{errors.terms.message}</p>}

        <button type="submit" disabled={isLoading} className="btn-primary w-full py-2.5">
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Creating account...
            </>
          ) : (
            <>
              <UserPlus size={16} />
              Create Account
            </>
          )}
        </button>
      </form>

      <div className="mt-5 text-center">
        <p className="text-sm text-muted-foreground">
          Already have an account?{' '}
          <button onClick={onSwitch} className="text-primary font-600 hover:underline" style={{ color: 'var(--primary)' }}>
            Sign In
          </button>
        </p>
      </div>
    </div>
  );
}